import React, { useState, useEffect, useMemo } from 'react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { useLanguage } from '@/contexts/LanguageContext';
import { Calendar } from 'lucide-react';

const HIJRI_MONTHS_EN = [
  'Muharram', 'Safar', 'Rabi\' al-Awwal', 'Rabi\' al-Thani',
  'Jumada al-Ula', 'Jumada al-Thani', 'Rajab', 'Sha\'ban',
  'Ramadan', 'Shawwal', 'Dhu al-Qi\'dah', 'Dhu al-Hijjah',
];

const HIJRI_MONTHS_AR = [
  'محرم', 'صفر', 'ربيع الأول', 'ربيع الآخر',
  'جمادى الأولى', 'جمادى الآخرة', 'رجب', 'شعبان',
  'رمضان', 'شوال', 'ذو القعدة', 'ذو الحجة',
];

function gregorianToHijri(date: Date): { year: number; month: number; day: number } {
  const formatter = new Intl.DateTimeFormat('en-u-ca-islamic-umalqura', {
    year: 'numeric', month: 'numeric', day: 'numeric',
  });
  const parts = formatter.formatToParts(date);
  return {
    year: Number(parts.find(p => p.type === 'year')?.value || 0),
    month: Number(parts.find(p => p.type === 'month')?.value || 1),
    day: Number(parts.find(p => p.type === 'day')?.value || 1),
  };
}

function hijriToGregorian(hYear: number, hMonth: number, hDay: number): Date {
  // Binary search: find the Gregorian date whose Hijri representation matches
  // Start with an approximate Gregorian date
  const approxGregorian = new Date(
    Math.round((hYear - 1) * 354.36667 + (hMonth - 1) * 29.53056 + hDay + 1948438.5 - 2451545) * 86400000 +
    Date.UTC(2000, 0, 1)
  );

  // Search within a window around the approximation
  let low = new Date(approxGregorian.getTime() - 5 * 86400000);
  let high = new Date(approxGregorian.getTime() + 5 * 86400000);

  for (let d = new Date(low); d <= high; d = new Date(d.getTime() + 86400000)) {
    const h = gregorianToHijri(d);
    if (h.year === hYear && h.month === hMonth && h.day === hDay) {
      return d;
    }
  }

  // Wider search if exact match not found
  low = new Date(approxGregorian.getTime() - 30 * 86400000);
  high = new Date(approxGregorian.getTime() + 30 * 86400000);
  let closest = approxGregorian;
  let closestDiff = Infinity;

  for (let d = new Date(low); d <= high; d = new Date(d.getTime() + 86400000)) {
    const h = gregorianToHijri(d);
    const diff = Math.abs(h.year - hYear) * 400 + Math.abs(h.month - hMonth) * 31 + Math.abs(h.day - hDay);
    if (diff < closestDiff) {
      closestDiff = diff;
      closest = new Date(d);
    }
    if (diff === 0) return d;
  }

  return closest;
}

function getDaysInHijriMonth(hYear: number, hMonth: number): number {
  // Find the first day of next month and calculate the difference
  const firstOfThisMonth = hijriToGregorian(hYear, hMonth, 1);
  const nextMonth = hMonth === 12 ? 1 : hMonth + 1;
  const nextYear = hMonth === 12 ? hYear + 1 : hYear;
  const firstOfNextMonth = hijriToGregorian(nextYear, nextMonth, 1);
  return Math.round((firstOfNextMonth.getTime() - firstOfThisMonth.getTime()) / 86400000);
}

interface HijriDatePickerProps {
  value: string;
  onChange: (value: string) => void;
  id?: string;
  className?: string;
  label?: string;
  labelClassName?: string;
}

export function HijriDatePicker({ value, onChange, id, className, label, labelClassName }: HijriDatePickerProps) {
  const { language } = useLanguage();
  const [mode, setMode] = useState<'gregorian' | 'hijri'>('gregorian');
  const [hijriYear, setHijriYear] = useState<string>('');
  const [hijriMonth, setHijriMonth] = useState<string>('');
  const [hijriDay, setHijriDay] = useState<string>('');

  const months = language === 'ar' ? HIJRI_MONTHS_AR : HIJRI_MONTHS_EN;

  // When value changes externally or mode switches, sync Hijri fields
  useEffect(() => {
    if (mode === 'hijri' && value) {
      try {
        const date = new Date(value);
        if (!isNaN(date.getTime())) {
          const h = gregorianToHijri(date);
          setHijriYear(String(h.year));
          setHijriMonth(String(h.month));
          setHijriDay(String(h.day));
        }
      } catch { /* ignore invalid dates */ }
    }
  }, [value, mode]);

  const daysInMonth = useMemo(() => {
    if (hijriYear && hijriMonth) {
      return getDaysInHijriMonth(Number(hijriYear), Number(hijriMonth));
    }
    return 30;
  }, [hijriYear, hijriMonth]);

  // Clamp day if it exceeds the month's length
  useEffect(() => {
    if (hijriDay && Number(hijriDay) > daysInMonth) {
      setHijriDay(String(daysInMonth));
    }
  }, [daysInMonth, hijriDay]);

  const handleHijriChange = (year: string, month: string, day: string) => {
    setHijriYear(year);
    setHijriMonth(month);
    setHijriDay(day);

    const y = Number(year);
    const m = Number(month);
    const d = Number(day);
    if (y && m && d && d <= getDaysInHijriMonth(y, m)) {
      const greg = hijriToGregorian(y, m, d);
      const iso = greg.toISOString().split('T')[0];
      onChange(iso);
    }
  };

  const hijriYears = useMemo(() => {
    const years: number[] = [];
    for (let y = 1500; y >= 1350; y--) years.push(y);
    return years;
  }, []);

  const selectClass = 'flex h-9 w-full rounded-md border border-input bg-transparent px-2 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50';

  return (
    <div className={className}>
      <div className="flex items-center justify-between mb-1.5">
        {label && <label className={labelClassName || 'text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70'}>{label}</label>}
        <div className="flex items-center gap-1 ms-auto">
          <Button
            type="button"
            variant={mode === 'gregorian' ? 'default' : 'outline'}
            size="sm"
            className="h-6 px-2 text-xs"
            onClick={() => setMode('gregorian')}
          >
            {language === 'ar' ? 'ميلادي' : 'Gregorian'}
          </Button>
          <Button
            type="button"
            variant={mode === 'hijri' ? 'default' : 'outline'}
            size="sm"
            className="h-6 px-2 text-xs"
            onClick={() => setMode('hijri')}
          >
            {language === 'ar' ? 'هجري' : 'Hijri'}
          </Button>
        </div>
      </div>

      {mode === 'gregorian' ? (
        <Input
          id={id}
          type="date"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          dir="ltr"
        />
      ) : (
        <div className="flex gap-1.5" dir="ltr">
          <select
            className={selectClass}
            value={hijriDay}
            onChange={(e) => handleHijriChange(hijriYear, hijriMonth, e.target.value)}
          >
            <option value="">{language === 'ar' ? 'يوم' : 'Day'}</option>
            {Array.from({ length: daysInMonth }, (_, i) => i + 1).map(d => (
              <option key={d} value={String(d)}>{d}</option>
            ))}
          </select>
          <select
            className={`${selectClass} min-w-[120px]`}
            value={hijriMonth}
            onChange={(e) => handleHijriChange(hijriYear, e.target.value, hijriDay)}
          >
            <option value="">{language === 'ar' ? 'شهر' : 'Month'}</option>
            {months.map((name, i) => (
              <option key={i + 1} value={String(i + 1)}>{name}</option>
            ))}
          </select>
          <select
            className={selectClass}
            value={hijriYear}
            onChange={(e) => handleHijriChange(e.target.value, hijriMonth, hijriDay)}
          >
            <option value="">{language === 'ar' ? 'سنة' : 'Year'}</option>
            {hijriYears.map(y => (
              <option key={y} value={String(y)}>{y}</option>
            ))}
          </select>
        </div>
      )}

      {mode === 'hijri' && value && (
        <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1" dir="ltr">
          <Calendar className="h-3 w-3" />
          {value}
        </p>
      )}
    </div>
  );
}
